<template>
  <v-container fluid class="pa-0">
    <v-row class="mb-4" align="center">
      <v-col cols="12" md="6">
        <h2 class="text-h4 white--text font-weight-bold">System Statistiken</h2>
        <div class="text-subtitle-1 grey--text">Übersicht: Roody App vs. TAC Verwaltung</div>
      </v-col>

      <v-col cols="12" md="6" class="d-flex justify-end align-center flex-wrap gap-2">
        <v-menu ref="menu" v-model="menu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="primary" outlined class="mr-2" v-bind="attrs" v-on="on">
              <v-icon left>mdi-calendar</v-icon>
              {{ formattedDate }}
            </v-btn>
          </template>
          <v-date-picker
            v-model="date"
            :max="today"
            no-title
            scrollable
            color="primary"
            dark
            @input="
              menu = false
              fetchDataNew()
            "></v-date-picker>
        </v-menu>

        <v-btn color="primary" @click="downloadCustomerWithoutRoodyList" :loading="downloading" class="mr-2">
          <v-icon left>mdi-account-off</v-icon>
          Ohne Account (XLSX)
        </v-btn>

        <v-btn v-show="false" icon @click="reloadStatistics" :loading="loading" color="white">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12">
        <div class="d-flex align-center mb-2">
          <v-icon color="primary" class="mr-2">mdi-cellphone</v-icon>
          <h3 class="text-h5 white--text font-weight-bold">Roody App Statistiken</h3>
        </div>
        <v-divider class="grey darken-3"></v-divider>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <v-card class="rounded-lg fill-height" elevation="0" color="#1E1E1E" dark>
          <v-card-text class="stat-kpi-card d-flex justify-space-between align-center fill-height">
            <div>
              <div class="text-overline grey--text">Registrierte Roody Nutzer</div>
              <div class="text-h3 font-weight-bold white--text">{{ kpiRoodyTotal }}</div>
            </div>
            <div class="stat-kpi-card__actions">
              <statistic-export-menu
                title="Registrierte Roody Nutzer"
                file-key="registrierte-roody-nutzer"
                :date="date"
                :categories="['Gesamt']"
                :series="[{ name: 'Nutzer', data: [Number(kpiRoodyTotal) || 0] }]"
                :summary="[{ label: 'Registrierte Nutzer', value: kpiRoodyTotal }]"
                :disabled="loading" />
              <v-avatar color="primary" rounded size="64" class="elevation-3">
                <v-icon x-large color="white">mdi-account-check</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text text-subtitle-1 d-flex justify-space-between">
            <span>Benutzer pro Studio (Check-in Status)</span>
            <div class="stat-card-actions">
              <statistic-export-menu
                title="Benutzer pro Studio (Check-in Status)"
                file-key="roody-benutzer-pro-studio"
                :date="date"
                :categories="chartOptionsRoodyCount.xaxis.categories"
                :series="seriesRoodyCount"
                :summary="[{ label: 'Registrierte Nutzer', value: kpiRoodyTotal }]"
                :disabled="loading" />
              <v-btn icon class="stat-action-btn" @click="presentChart('Benutzer pro Studio (Roody)', 'bar', chartOptionsRoodyCount, seriesRoodyCount)">
                <v-icon color="grey lighten-1">mdi-fullscreen</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <div v-if="loading" class="d-flex justify-center py-10">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <apexchart v-else type="bar" height="300" :options="chartOptionsRoodyCount" :series="seriesRoodyCount"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text text-subtitle-1 d-flex justify-space-between">
            <span>Geschlecht (Roody)</span>
            <div class="stat-card-actions">
              <statistic-export-menu
                title="Geschlecht (Roody)"
                file-key="roody-geschlecht"
                :date="date"
                :series="seriesRoodyGender"
                :summary="[{ label: 'Registrierte Nutzer', value: kpiRoodyTotal }]"
                :disabled="loading" />
              <v-btn icon class="stat-action-btn" @click="presentChart('Geschlecht (Roody)', 'bar', chartOptionsGender, seriesRoodyGender)">
                <v-icon color="grey lighten-1">mdi-fullscreen</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <apexchart v-if="!loading" type="bar" height="250" :options="chartOptionsGender" :series="seriesRoodyGender"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text text-subtitle-1 d-flex justify-space-between">
            <div class="d-flex align-center">
              <span>Altersstruktur (Roody)</span>
              <v-chip v-if="averageAgeRoody > 0" color="primary" outlined class="ml-2"> Ø {{ averageAgeRoody }} Jahre </v-chip>
            </div>
            <div class="stat-card-actions">
              <statistic-export-menu
                title="Altersstruktur (Roody)"
                file-key="roody-altersstruktur"
                :date="date"
                :series="seriesRoodyAge"
                :summary="[
                  { label: 'Durchschnittsalter', value: averageAgeRoody + ' Jahre' },
                  { label: 'Registrierte Nutzer', value: kpiRoodyTotal },
                ]"
                :disabled="loading" />
              <v-btn icon class="stat-action-btn" @click="presentChart('Altersstruktur (Roody)', 'bar', chartOptionsAge, seriesRoodyAge)">
                <v-icon color="grey lighten-1">mdi-fullscreen</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <apexchart v-if="!loading" type="bar" height="250" :options="chartOptionsAge" :series="seriesRoodyAge"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-6">
      <v-col cols="12">
        <div class="d-flex align-center mb-2">
          <v-icon color="success" class="mr-2">mdi-desktop-tower-monitor</v-icon>
          <h3 class="text-h5 white--text font-weight-bold">TAC Statistiken</h3>
        </div>
        <v-divider class="grey darken-3"></v-divider>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <v-card class="rounded-lg fill-height" elevation="0" color="#1E1E1E" dark>
          <v-card-text class="stat-kpi-card d-flex justify-space-between align-center fill-height">
            <div>
              <div class="text-overline grey--text">Aktive Mitglieder (TAC)</div>
              <div class="text-h3 font-weight-bold white--text">{{ kpiTACTotal }}</div>
            </div>
            <div class="stat-kpi-card__actions">
              <statistic-export-menu
                title="Aktive Mitglieder (TAC)"
                file-key="aktive-mitglieder-tac"
                :date="date"
                :categories="['Gesamt']"
                :series="[{ name: 'Mitglieder', data: [Number(kpiTACTotal) || 0] }]"
                :summary="[{ label: 'Aktive Mitglieder', value: kpiTACTotal }]"
                accent="#00e396"
                :disabled="loading" />
              <v-avatar color="success" rounded size="64" class="elevation-3">
                <v-icon x-large color="white">mdi-card-account-details</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text text-subtitle-1 d-flex justify-space-between">
            <span>Mitglieder pro Studio (Roody Abdeckung)</span>
            <div class="stat-card-actions">
              <statistic-export-menu
                title="Mitglieder pro Studio (Roody Abdeckung)"
                file-key="tac-mitglieder-pro-studio"
                :date="date"
                :categories="chartOptionsTACCount.xaxis.categories"
                :series="seriesTACCount"
                :summary="[{ label: 'Aktive Mitglieder', value: kpiTACTotal }]"
                accent="#00e396"
                :disabled="loading" />
              <v-btn icon class="stat-action-btn" @click="presentChart('Mitglieder pro Studio (TAC)', 'bar', chartOptionsTACCount, seriesTACCount)">
                <v-icon color="grey lighten-1">mdi-fullscreen</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <div v-if="loading" class="d-flex justify-center py-10">
              <v-progress-circular indeterminate color="success"></v-progress-circular>
            </div>
            <apexchart v-else type="bar" height="300" :options="chartOptionsTACCount" :series="seriesTACCount"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text text-subtitle-1 d-flex justify-space-between">
            <span>Abonnement Verteilung</span>
            <div class="stat-card-actions">
              <statistic-export-menu
                :title="'Abonnements (' + (tacTab === 0 ? 'Detail' : 'Gruppiert') + ')'"
                :file-key="tacTab === 0 ? 'tac-abonnements-detail' : 'tac-abonnements-gruppiert'"
                :date="date"
                :categories="tacTab === 0 ? chartOptionsTACSubs.xaxis.categories : chartOptionsTACSubsGrouped.xaxis.categories"
                :series="tacTab === 0 ? seriesTACSubs : seriesTACSubsGrouped"
                :summary="[{ label: 'Aktive Mitglieder', value: kpiTACTotal }]"
                accent="#00e396"
                :disabled="loading" />
              <v-btn
                icon
                class="stat-action-btn"
                @click="
                  presentChart(
                    'Abonnements (' + (tacTab === 0 ? 'Detail' : 'Gruppiert') + ')',
                    'bar',
                    tacTab === 0 ? chartOptionsTACSubs : chartOptionsTACSubsGrouped,
                    tacTab === 0 ? seriesTACSubs : seriesTACSubsGrouped,
                  )
                ">
                <v-icon color="grey lighten-1">mdi-fullscreen</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-tabs v-model="tacTab" background-color="transparent" dark>
            <v-tab>Abo Detail</v-tab>
            <v-tab>Abo Gruppiert</v-tab>
          </v-tabs>
          <v-card-text>
            <v-tabs-items v-model="tacTab" style="background-color: transparent">
              <v-tab-item>
                <apexchart v-if="!loading" type="bar" height="400" :options="chartOptionsTACSubs" :series="seriesTACSubs"></apexchart>
              </v-tab-item>
              <v-tab-item>
                <apexchart v-if="!loading" type="bar" height="400" :options="chartOptionsTACSubsGrouped" :series="seriesTACSubsGrouped"></apexchart>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text text-subtitle-1 d-flex justify-space-between">
            <span>Geschlecht (TAC)</span>
            <div class="stat-card-actions">
              <statistic-export-menu
                title="Geschlecht (TAC)"
                file-key="tac-geschlecht"
                :date="date"
                :series="seriesTACGender"
                :summary="[{ label: 'Aktive Mitglieder', value: kpiTACTotal }]"
                accent="#00e396"
                :disabled="loading" />
              <v-btn icon class="stat-action-btn" @click="presentChart('Geschlecht (TAC)', 'bar', chartOptionsGender, seriesTACGender)">
                <v-icon color="grey lighten-1">mdi-fullscreen</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <apexchart v-if="!loading" type="bar" height="250" :options="chartOptionsGender" :series="seriesTACGender"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="12">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text text-subtitle-1 d-flex justify-space-between">
            <span>Top 20 PLZ Verteilung (TAC)</span>
            <div class="stat-card-actions">
              <statistic-export-menu
                title="Top 20 PLZ Verteilung (TAC)"
                file-key="tac-top-20-plz"
                :date="date"
                :series="seriesTACPlz"
                :summary="[{ label: 'PLZ-Gebiete', value: seriesTACPlz.length ? seriesTACPlz[0].data.length : 0 }]"
                accent="#9c27b0"
                :disabled="loading" />
              <v-btn icon class="stat-action-btn" @click="presentChart('Top 20 PLZ (TAC)', 'bar', chartOptionsTACPlz, seriesTACPlz)">
                <v-icon color="grey lighten-1">mdi-fullscreen</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <apexchart v-if="!loading" type="bar" height="300" :options="chartOptionsTACPlz" :series="seriesTACPlz"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12">
        <catchment-map :map-data="mapDataTAC" :studios="studios" :export-date="date"></catchment-map>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text text-subtitle-1 d-flex justify-space-between">
            <div class="d-flex align-center">
              <span>Altersstruktur (TAC)</span>
              <v-chip v-if="averageAgeTAC > 0" color="success" outlined class="ml-2"> Ø {{ averageAgeTAC }} Jahre </v-chip>
            </div>
            <div class="stat-card-actions">
              <statistic-export-menu
                title="Altersstruktur (TAC)"
                file-key="tac-altersstruktur"
                :date="date"
                :series="seriesTACAge"
                :summary="[
                  { label: 'Durchschnittsalter', value: averageAgeTAC + ' Jahre' },
                  { label: 'Aktive Mitglieder', value: kpiTACTotal },
                ]"
                accent="#00e396"
                :disabled="loading" />
              <v-btn icon class="stat-action-btn" @click="presentChart('Altersstruktur (TAC)', 'bar', chartOptionsAge, seriesTACAge)">
                <v-icon color="grey lighten-1">mdi-fullscreen</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <apexchart v-if="!loading" type="bar" height="250" :options="chartOptionsAge" :series="seriesTACAge"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text text-subtitle-1 d-flex justify-space-between">
            <span>Kunden pro m²</span>
            <div class="stat-card-actions">
              <statistic-export-menu
                title="Kunden pro m²"
                file-key="tac-kunden-pro-quadratmeter"
                :date="date"
                :categories="chartOptionsTACm2.xaxis.categories"
                :series="seriesTACm2"
                accent="#008ffb"
                :disabled="loading" />
              <v-btn icon class="stat-action-btn" @click="presentChart('Kunden pro m²', 'bar', chartOptionsTACm2, seriesTACm2)">
                <v-icon color="grey lighten-1">mdi-fullscreen</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <apexchart v-if="!loading" type="bar" height="250" :options="chartOptionsTACm2" :series="seriesTACm2"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="rounded-lg" elevation="0" color="#1E1E1E" dark>
          <v-card-title class="white--text text-subtitle-1 d-flex justify-space-between">
            <span>Kunden pro 100% Trainer</span>
            <div class="stat-card-actions">
              <statistic-export-menu
                title="Kunden pro 100% Trainer"
                file-key="tac-kunden-pro-trainer"
                :date="date"
                :categories="chartOptionsTACTrainer.xaxis.categories"
                :series="seriesTACTrainer"
                accent="#feb019"
                :disabled="loading" />
              <v-btn icon class="stat-action-btn" @click="presentChart('Kunden pro 100% Trainer', 'bar', chartOptionsTACTrainer, seriesTACTrainer)">
                <v-icon color="grey lighten-1">mdi-fullscreen</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <apexchart v-if="!loading" type="bar" height="250" :options="chartOptionsTACTrainer" :series="seriesTACTrainer"></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="presentationDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card color="#121212" dark tile class="d-flex flex-column" height="100vh">
        <v-toolbar dark color="primary" class="flex-grow-0" style="z-index: 5">
          <v-btn icon dark @click="presentationDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ presentationData.title }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark text @click="presentationDialog = false">Schliessen</v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <div class="d-flex align-center justify-center pa-4" style="height: calc(100vh - 64px); width: 100%; background-color: #121212">
          <div style="width: 95%; height: 90%">
            <apexchart
              v-if="presentationDialog"
              :type="presentationData.type"
              width="100%"
              height="100%"
              :options="presentationData.options"
              :series="presentationData.series"></apexchart>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "../../../api"
import { saveAs } from "file-saver"
import userService from "@/services/userService"
import CatchmentMap from "./CatchmentMap.vue"
import StatisticExportMenu from "./StatisticExportMenu.vue"
export default {
  name: "DashboardStatistics",
  components: { CatchmentMap, StatisticExportMenu },
  props: {
    studios: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      today: new Date().toISOString().substr(0, 10),
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
      menu: false,
      tacTab: 1,

      // State
      loading: false,
      downloading: false,
      dashboardUserStatistics: {}, // Roody Data
      dashboardUserTACStatistics: {}, // TAC Data

      // Presentation Mode
      presentationDialog: false,
      presentationData: {
        title: "",
        type: "bar",
        options: {},
        series: [],
      },

      userService,
    }
  },
  computed: {
    averageAgeRoody() {
      return this.calculateAverageAge(this.seriesRoodyAge)
    },
    averageAgeTAC() {
      return this.calculateAverageAge(this.seriesTACAge)
    },
    formattedDate() {
      return this.date // Or use moment if available: this.$moment(this.date).format('DD.MM.YYYY')
    },
    // --- KPIs ---
    kpiRoodyTotal() {
      const series = this.dashboardUserStatistics.ageSeriesData || []
      let total = 0
      series.forEach((item) => {
        if (item.data) total += item.data.reduce((a, b) => a + (b.y || 0), 0)
      })
      return total > 0 ? total : "0"
    },
    kpiTACTotal() {
      return this.dashboardUserTACStatistics.activeTACCustomerAmount || 0
    },

    // --- ROODY SERIES ---
    seriesRoodyCount() {
      const data = this.dashboardUserStatistics.userCountSeries || []
      const sorted = [...data].sort((a, b) => b.usersWithCheckins + b.usersWithoutCheckins - (a.usersWithCheckins + a.usersWithoutCheckins))

      return [
        { name: "Mit Check-in", data: sorted.map((i) => i.usersWithCheckins) },
        { name: "Ohne Check-in", data: sorted.map((i) => i.usersWithoutCheckins) },
      ]
    },
    seriesRoodyGender() {
      return this.dashboardUserStatistics.userGenderSeries || []
    },
    seriesRoodyAge() {
      return this.dashboardUserStatistics.ageSeriesData || []
    },

    // --- TAC SERIES ---
    seriesTACPlz() {
      // Das ist jetzt automatisch nur noch Top 20 (kommt so vom Backend als userPlzSeriesTAC)
      return this.dashboardUserTACStatistics.userPlzSeriesTAC || []
    },

    // NEU: Computed Property für die Map Daten
    mapDataTAC() {
      // Das sind alle Daten für die Map
      return this.dashboardUserTACStatistics.userPlzMapDataTAC || []
    },
    seriesTACCount() {
      const data = this.dashboardUserTACStatistics.activeTACUsers || []
      const sorted = [...data].sort((a, b) => b.emailsFoundAmount + b.restAmount - (a.emailsFoundAmount + a.restAmount))

      return [
        { name: "Mit Roody Account", data: sorted.map((i) => i.emailsFoundAmount) },
        { name: "Ohne Roody Account", data: sorted.map((i) => i.restAmount) },
      ]
    },
    seriesTACSubs() {
      return this.processSubscriptionData(this.dashboardUserTACStatistics.TACUsersBySubscriptionType)
    },
    seriesTACSubsGrouped() {
      return this.processSubscriptionData(this.dashboardUserTACStatistics.TACUsersBySubscriptionTypeGrouped)
    },
    seriesTACGender() {
      return this.dashboardUserTACStatistics.userGenderSeriesTAC || []
    },
    seriesTACAge() {
      return this.dashboardUserTACStatistics.userAgeSeriesTAC || []
    },
    seriesTACm2() {
      const data = this.dashboardUserTACStatistics.activeTACUsersArea || []
      return [{ name: "Kunden/m²", data: [...data].sort((a, b) => b.amount - a.amount).map((i) => i.amount) }]
    },
    seriesTACTrainer() {
      const data = this.dashboardUserTACStatistics.customerPerTrainerTAC || []
      return [{ name: "Kunden/Trainer", data: [...data].sort((a, b) => b.amount - a.amount).map((i) => i.amount) }]
    },

    // --- CHART OPTIONS GENERATORS ---
    chartOptionsTACPlz() {
      // Wir nutzen eine ähnliche Config wie bei Gender/Age, aber wir wollen die Labels unten gut sehen
      return {
        ...this.getBaseChartOptions([], true), // true = stacked (spielt hier keine Rolle da nur 1 Serie), holt basic styles
        xaxis: {
          // Überschreibt die categories aus getBaseChartOptions, da die Daten im Format {x, y} kommen
          // ApexCharts zieht sich die Labels bei {x, y} automatisch, aber wir stellen sicher, dass Styles passen
          labels: {
            style: { colors: "#ffffff" },
            rotate: -45, // Schräg, damit die PLZ Platz haben
            trim: false,
          },
          type: "category",
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: false,
            columnWidth: "60%",
            distributed: false, // Alle Balken gleiche Farbe (oder true für bunt)
          },
        },
        colors: ["#9C27B0"], // Eine definierte Farbe (z.B. Lila) für die PLZ Balken
      }
    },
    chartOptionsRoodyCount() {
      const data = this.dashboardUserStatistics.userCountSeries || []
      const categories = [...data]
        .sort((a, b) => b.usersWithCheckins + b.usersWithoutCheckins - (a.usersWithCheckins + a.usersWithoutCheckins))
        .map((i) => i.location)
      return this.getBaseChartOptions(categories, true, ["#2ecc71", "#34495e"])
    },
    chartOptionsTACCount() {
      const data = this.dashboardUserTACStatistics.activeTACUsers || []
      const categories = [...data].sort((a, b) => b.emailsFoundAmount + b.restAmount - (a.emailsFoundAmount + a.restAmount)).map((i) => i.location)
      return this.getBaseChartOptions(categories, true, ["#00E396", "#FF4560"])
    },
    chartOptionsTACSubs() {
      const data = this.dashboardUserTACStatistics.TACUsersBySubscriptionType || []
      const categories = [...data].sort((a, b) => b.length - a.length).map((i) => i.location)
      return this.getBaseChartOptions(categories, true)
    },
    chartOptionsTACSubsGrouped() {
      const data = this.dashboardUserTACStatistics.TACUsersBySubscriptionTypeGrouped || []
      const categories = [...data].sort((a, b) => b.length - a.length).map((i) => i.location)
      return this.getBaseChartOptions(categories, true)
    },
    chartOptionsGender() {
      return {
        ...this.getBaseChartOptions([], true),
        plotOptions: { bar: { distributed: true, borderRadius: 4, horizontal: false } },
        xaxis: { labels: { style: { colors: "#fff" } } },
      }
    },
    chartOptionsAge() {
      return {
        ...this.getBaseChartOptions([], true),
        xaxis: { labels: { show: true, style: { colors: "#fff" } } },
      }
    },
    chartOptionsTACm2() {
      const data = this.dashboardUserTACStatistics.activeTACUsersArea || []
      const categories = [...data].sort((a, b) => b.amount - a.amount).map((i) => i.location)
      return this.getBaseChartOptions(categories, false, ["#008FFB"])
    },
    chartOptionsTACTrainer() {
      const data = this.dashboardUserTACStatistics.customerPerTrainerTAC || []
      const categories = [...data].sort((a, b) => b.amount - a.amount).map((i) => i.location)
      return this.getBaseChartOptions(categories, false, ["#FEB019"])
    },
  },
  watch: {
    studios: {
      handler() {
        this.fetchDataNew()
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchDataNew()
  },
  methods: {
    calculateAverageAge(seriesData) {
      if (!seriesData || seriesData.length === 0) return 0

      let totalAgeAccumulated = 0
      let totalPeople = 0

      seriesData.forEach((series) => {
        if (series.data) {
          series.data.forEach((point) => {
            // Wir gehen davon aus, dass point.x das Alter (string/int) und point.y die Anzahl ist
            // Falls point.x ein String ist wie "25", funktioniert parseInt.
            // Falls es eine Range ist (z.B. "20-30"), nimmt parseInt die erste Zahl (20).
            const age = parseInt(point.x)
            const count = point.y || 0

            if (!isNaN(age) && count > 0) {
              totalAgeAccumulated += age * count
              totalPeople += count
            }
          })
        }
      })

      if (totalPeople === 0) return 0

      // Rückgabe mit einer Nachkommastelle, z.B. "28.5"
      return (totalAgeAccumulated / totalPeople).toFixed(1)
    },
    // --- Presentation Method ---
    presentChart(title, type, options, series) {
      // 1. Daten kopieren
      this.presentationData = {
        title,
        type,
        options: JSON.parse(JSON.stringify(options)),
        series: JSON.parse(JSON.stringify(series)),
      }

      // 2. Optionen für Big-Screen optimieren (Grössere Schriftarten)
      const bigFont = "16px"
      const hugeFont = "24px" // Für DataLabels

      // X-Achse
      if (!this.presentationData.options.xaxis) this.presentationData.options.xaxis = {}
      if (!this.presentationData.options.xaxis.labels) this.presentationData.options.xaxis.labels = {}
      if (!this.presentationData.options.xaxis.labels.style) this.presentationData.options.xaxis.labels.style = {}

      this.presentationData.options.xaxis.labels.style.fontSize = bigFont
      this.presentationData.options.xaxis.labels.rotate = -45 // Schräg stellen, damit mehr Text passt

      // Legende
      if (!this.presentationData.options.legend) this.presentationData.options.legend = {}
      this.presentationData.options.legend.fontSize = bigFont
      this.presentationData.options.legend.position = "bottom" // Unten ist auf Beamern oft besser

      // Data Labels (die Zahlen im Balken)
      if (!this.presentationData.options.dataLabels) this.presentationData.options.dataLabels = {}
      if (!this.presentationData.options.dataLabels.style) this.presentationData.options.dataLabels.style = {}
      this.presentationData.options.dataLabels.style.fontSize = "14px"
      this.presentationData.options.dataLabels.enabled = true // Immer anzeigen im Presentation Mode

      // Y-Achse auch lesbar machen
      if (!this.presentationData.options.yaxis) this.presentationData.options.yaxis = {}
      if (!this.presentationData.options.yaxis.labels) this.presentationData.options.yaxis.labels = {}
      if (!this.presentationData.options.yaxis.labels.style) this.presentationData.options.yaxis.labels.style = {}
      this.presentationData.options.yaxis.labels.style.fontSize = bigFont
      this.presentationData.options.yaxis.show = true // Y-Achse anzeigen für Kontext

      // 3. Dialog öffnen
      this.presentationDialog = true
    },

    async fetchDataNew() {
      this.loading = true
      try {
        const studioIds = this.studios.map((e) => e._id)
        const statistics = await api.fetchDashboardUserStatisticsNew({
          studios: studioIds,
          date: this.date,
        })
        this.dashboardUserTACStatistics = statistics.tacStatistics || {}
        this.dashboardUserStatistics = statistics.statistics || {}
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Laden der Daten")
      } finally {
        this.loading = false
      }
    },

    async reloadStatistics() {
      this.loading = true
      try {
        await api.reloadDashboardUserStatistics()
        this.$toast.success("Statistiken im Hintergrund aktualisiert")
        await this.fetchDataNew()
      } catch (e) {
        this.$toast.error("Update fehlgeschlagen")
      } finally {
        this.loading = false
      }
    },

    async downloadCustomerWithoutRoodyList() {
      this.downloading = true
      try {
        let status = await api.downloadUsersWithoutRoodyList()
        const fileName = "TACOhneRoody_" + (this.$moment ? this.$moment().format("YYYY-MM-DD") : this.date)

        if (window.flutter_inappwebview) {
          // FLUTTER BRIDGE LOGIC
          var reader = new FileReader()
          reader.readAsDataURL(status)
          reader.onloadend = function () {
            var base64 = reader.result.split(",")[1]
            window.flutter_inappwebview.callHandler("blobToBase64Handler", base64.toString(), "xlsx", fileName)
          }
        } else {
          // WEB LOGIC
          saveAs(status, fileName)
        }
      } catch (e) {
        this.$toast.error("Download fehlgeschlagen")
      } finally {
        this.downloading = false
      }
    },

    processSubscriptionData(data) {
      if (!data) return []
      const sorted = [...data].sort((a, b) => b.length - a.length)

      // Collect all unique keys except meta keys
      const keys = new Set()
      data.forEach((item) => {
        Object.keys(item).forEach((k) => {
          if (k !== "location" && k !== "length") keys.add(k)
        })
      })

      const series = []
      keys.forEach((key) => {
        series.push({
          name: key,
          data: sorted.map((item) => item[key] || 0),
        })
      })
      return series
    },

    getBaseChartOptions(categories = [], stacked = true, colors = null) {
      const opts = {
        chart: {
          background: "transparent",
          foreColor: "#ffffff",
          stacked: stacked,
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        theme: { mode: "dark" },
        plotOptions: {
          bar: {
            horizontal: false, // Vertical bars by default
            borderRadius: 4,
            columnWidth: "55%",
            dataLabels: {
              position: "top", // for non-stacked
            },
          },
        },
        dataLabels: { enabled: false }, // usually too messy for stacked
        xaxis: {
          categories: categories,
          labels: { style: { colors: "#ffffff" }, rotate: -45, trim: true },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: "#ffffff" } } },
        grid: { borderColor: "#333" },
        tooltip: {
          theme: "dark",
          y: {
            formatter: function (val) {
              return val ? val.toFixed(0) : 0
            },
          },
        },
        legend: { position: "top", labels: { colors: "#ffffff" } },
      }

      if (colors) opts.colors = colors

      return opts
    },
  },
}
</script>

<style scoped>
::v-deep .v-card {
  border: 1px solid #333;
}
/* Better gap support */
.gap-2 {
  gap: 0.5rem;
}

.stat-card-actions,
.stat-kpi-card__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.375rem;
}

.stat-action-btn {
  width: 2.5rem;
  height: 2.5rem;
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.035);
}

.stat-action-btn:hover {
  border-color: rgba(88, 211, 136, 0.45);
  background: rgba(88, 211, 136, 0.1);
}

.stat-kpi-card {
  gap: 1rem;
}

@media (max-width: 37.5rem) {
  .stat-kpi-card {
    align-items: flex-start !important;
  }

  .v-card__title {
    align-items: flex-start;
    gap: 0.75rem;
    line-height: 1.4;
  }
}
</style>
